/**
 * IDP-1300: expiry badge (app cards) and expiry banner with Extend (app page).
 */
import { default as React, useState } from 'react';
import {
  Button,
  CircularProgress,
  Grid,
  makeStyles,
  Tooltip,
} from '@material-ui/core';
import Alert from '@mui/material/Alert';
import EventIcon from '@mui/icons-material/Event';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import {
  describeExpiry,
  formatExpiryDate,
  type ExpiryTone,
  type PublicAppExpiryInfo,
} from './publicAppExpiryApi';

const TONE_COLORS: Record<ExpiryTone, { color: string; background: string }> = {
  ok: { color: '#455a64', background: 'rgba(69, 90, 100, 0.1)' },
  warning: { color: '#b26a00', background: 'rgba(237, 108, 2, 0.12)' },
  danger: { color: '#c62828', background: 'rgba(198, 40, 40, 0.1)' },
};

const useStyles = makeStyles({
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    padding: '3px 6px',
    borderRadius: 4,
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    lineHeight: 1,
  },
  ok: TONE_COLORS.ok,
  warning: TONE_COLORS.warning,
  danger: TONE_COLORS.danger,
  icon: { fontSize: 12 },
  extendButton: { textTransform: 'none', whiteSpace: 'nowrap' },
});

/** Small badge for the app card, e.g. "EXPIRES IN 5 DAYS". */
export const PublicAppExpiryBadge = ({
  expiry,
}: {
  expiry: PublicAppExpiryInfo | undefined;
}) => {
  const classes = useStyles();
  if (!expiry) return null;
  const { label, tone } = describeExpiry(expiry.daysLeft);
  return (
    <Tooltip
      title={`Expires on ${formatExpiryDate(
        expiry.expiresAt,
      )} — the app and all its resources are then deleted. Redeploy or Extend to keep it.`}
    >
      <span
        className={`${classes.badge} ${classes[tone]}`}
        data-testid="public-app-expiry-badge"
      >
        <EventIcon className={classes.icon} />
        {label}
      </span>
    </Tooltip>
  );
};

const SEVERITY: Record<ExpiryTone, 'info' | 'warning' | 'error'> = {
  ok: 'info',
  warning: 'warning',
  danger: 'error',
};

/** Banner on the app page: expiry date, what happens, and Extend for owners. */
export const PublicAppExpiryBanner = ({
  expiry,
  expiryDays,
  maxExpiryDays,
  canExtend,
  onExtend,
}: {
  expiry: PublicAppExpiryInfo | undefined;
  expiryDays: number | undefined;
  /** Furthest Extend can push the date, in days from today. */
  maxExpiryDays?: number;
  canExtend: boolean;
  onExtend: () => Promise<PublicAppExpiryInfo>;
}) => {
  const classes = useStyles();
  const alertApi = useApi(alertApiRef);
  const [extending, setExtending] = useState(false);
  if (!expiry) return null;

  const { tone } = describeExpiry(expiry.daysLeft);
  const date = formatExpiryDate(expiry.expiresAt);
  const period = expiryDays ?? 30;
  const maxDays = maxExpiryDays ?? period * 3;
  // At the cap Extend would not move the date, so the button is disabled.
  const atMax = expiry.daysLeft >= maxDays;
  let headline: string;
  if (expiry.daysLeft <= 0) {
    headline = `This app expired on ${date} and is being deleted.`;
  } else if (expiry.daysLeft === 1) {
    headline = `This app expires on ${date} (in 1 day).`;
  } else {
    headline = `This app expires on ${date} (in ${expiry.daysLeft} days).`;
  }

  const handleExtend = async () => {
    setExtending(true);
    try {
      const info = await onExtend();
      alertApi.post({
        message: `Expiry extended to ${formatExpiryDate(info.expiresAt)}.`,
        severity: 'success',
        display: 'transient',
      });
    } catch (err: unknown) {
      alertApi.post({
        message: `Could not extend the expiry date: ${
          err instanceof Error ? err.message : String(err)
        }`,
        severity: 'error',
      });
    } finally {
      setExtending(false);
    }
  };

  return (
    <Grid item xs={12}>
      <Alert
        severity={SEVERITY[tone]}
        icon={<EventIcon />}
        action={
          canExtend && expiry.daysLeft > 0 ? (
            <Button
              color="inherit"
              variant="outlined"
              size="small"
              className={classes.extendButton}
              onClick={handleExtend}
              disabled={extending || atMax}
              title={
                atMax
                  ? `Already at the maximum of ${maxDays} days from today`
                  : undefined
              }
              startIcon={
                extending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
            >
              {extending ? 'Extending…' : `Extend ${period} days`}
            </Button>
          ) : undefined
        }
      >
        <strong>{headline}</strong> Expired apps are deleted with all their
        resources. Extend adds {period} days to this date (up to {maxDays} days
        from today); redeploying keeps at least {period} days.
      </Alert>
    </Grid>
  );
};
